import _extends from "@babel/runtime/helpers/extends";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useContext, useState, useEffect, createContext } from 'react';
import { formatRemoteString, getDisplayString, performSubstitution } from './utils';
import { LEARN_TIPS_URL, REMOTE_STRINGS_URL } from './constants';
export var RemoteStringsContext = /*#__PURE__*/createContext({});
export var RemoteStringsProvider = function RemoteStringsProvider(_ref) {
  var children = _ref.children;

  var _useState = useState(true),
      _useState2 = _slicedToArray(_useState, 2),
      isLoadingRemoteStrings = _useState2[0],
      setIsLoadingRemoteStrings = _useState2[1];

  var _useState3 = useState({}),
      _useState4 = _slicedToArray(_useState3, 2),
      localizedStrings = _useState4[0],
      setLocalizedStrings = _useState4[1];

  var _useState5 = useState(true),
      _useState6 = _slicedToArray(_useState5, 2),
      isLoadingLearnTips = _useState6[0],
      setIsLoadingLearnTips = _useState6[1];

  var _useState7 = useState({}),
      _useState8 = _slicedToArray(_useState7, 2),
      learnTips = _useState8[0],
      setLearnTips = _useState8[1];

  useEffect(function () {
    var mounted = true;

    var fetchRemoteStrings = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var remoteStringsResponse, stringsPayload, learnTipsResponse, learnTipsPayload;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return fetch(REMOTE_STRINGS_URL);

              case 2:
                remoteStringsResponse = _context.sent;
                _context.next = 5;
                return remoteStringsResponse.json();

              case 5:
                stringsPayload = _context.sent;
                _context.next = 8;
                return fetch(LEARN_TIPS_URL);

              case 8:
                learnTipsResponse = _context.sent;
                _context.next = 11;
                return learnTipsResponse.json();

              case 11:
                learnTipsPayload = _context.sent;

                if (mounted) {
                  setLocalizedStrings(stringsPayload['localized_strings']);
                  setIsLoadingRemoteStrings(false);
                  setLearnTips(learnTipsPayload);
                  setIsLoadingLearnTips(false);
                }

              case 13:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function fetchRemoteStrings() {
        return _ref2.apply(this, arguments);
      };
    }();

    if (isLoadingRemoteStrings && isLoadingLearnTips) {
      fetchRemoteStrings();
    } // RTL complaining about setting state before mount. Easiest workaround.


    return function () {
      mounted = false;
    };
  }, [isLoadingRemoteStrings, isLoadingLearnTips]);
  return /*#__PURE__*/React.createElement(RemoteStringsContext.Provider, {
    value: {
      isLoadingRemoteStrings: isLoadingRemoteStrings,
      localizedStrings: localizedStrings,
      isLoadingLearnTips: isLoadingLearnTips,
      learnTips: learnTips
    }
  }, children);
};
/**
 * Component for styling our remote strings
 *
 * @param stringKey - The remote string key
 * @param variables - The remote string variables that will replace the %N$s placeholders.
 * @param wrappers - Styled components that can be passed in to style substrings wrapped in ** **. See RemoteStringProvider.stories.tsx for examples.
 * @param modifiers - An array of objects with flags indicating how we want to modify substrings wrapped in ** **, e.g. bold, italic, underined. See RemoteStringProvider.stories.tsx for examples
 * @param nodes - An array of react nodes that will replace substrings wrapped in ** **. See RemoteStringProvider.stories.tsx for examples.
 * @param index- This is used purely for keying multiple of the same string on a page, we MUST pass in a unique index for each.
 * @param screen - This is passed in for learn tip strings events
 * @returns Returns a react node of the string.
 */

export var StyledRemoteString = function StyledRemoteString(_ref3) {
  var stringKey = _ref3.stringKey,
      _ref3$variables = _ref3.variables,
      variables = _ref3$variables === void 0 ? [] : _ref3$variables,
      _ref3$wrappers = _ref3.wrappers,
      wrappers = _ref3$wrappers === void 0 ? [] : _ref3$wrappers,
      _ref3$modifiers = _ref3.modifiers,
      modifiers = _ref3$modifiers === void 0 ? [] : _ref3$modifiers,
      _ref3$nodes = _ref3.nodes,
      nodes = _ref3$nodes === void 0 ? [] : _ref3$nodes,
      _ref3$index = _ref3.index,
      index = _ref3$index === void 0 ? 0 : _ref3$index,
      screen = _ref3.screen;

  var _useContext = useContext(RemoteStringsContext),
      localizedStrings = _useContext.localizedStrings,
      learnTips = _useContext.learnTips;

  var remoteString = localizedStrings[stringKey] || '';
  var substitutedString = performSubstitution(remoteString, variables);
  var renderKey = "".concat(stringKey, "-").concat(index);
  var formattedStringsArray = formatRemoteString(substitutedString, renderKey, localizedStrings, learnTips, modifiers, wrappers, nodes, screen);
  return /*#__PURE__*/React.createElement("span", {
    "data-testid": "string-".concat(renderKey)
  }, formattedStringsArray);
};
export var useRemoteStrings = function useRemoteStrings() {
  var _useContext2 = useContext(RemoteStringsContext),
      localizedStrings = _useContext2.localizedStrings,
      isLoadingRemoteStrings = _useContext2.isLoadingRemoteStrings;

  var getRemoteString = function getRemoteString(_ref4) {
    var key = _ref4.key,
        variables = _ref4.variables,
        color = _ref4.color;
    var remoteString = getDisplayString({
      strings: localizedStrings,
      key: key,
      variables: variables,
      variableColor: color
    });
    return remoteString;
  };

  return {
    isLoadingRemoteStrings: isLoadingRemoteStrings,
    getRemoteString: getRemoteString,
    StyledRemoteString: StyledRemoteString
  };
};
export var withRemoteStrings = function withRemoteStrings(Component) {
  return function (_ref5) {
    var props = _extends({}, _ref5);

    return /*#__PURE__*/React.createElement(RemoteStringsContext.Consumer, null, function (remoteStringsState) {
      return /*#__PURE__*/React.createElement(Component, _extends({
        remoteStringsState: remoteStringsState
      }, props));
    });
  };
};